import { Link, useParams } from 'react-router'
import { ROUTE_ORDERS, ROUTE_ORDER_EVENTS } from '@klear/constants'
import { PaymentStatusBadge } from '@klear/ui/components/payment-status-badge'
import { Label } from '@klear/ui/components/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@klear/ui/components/card'
import { trpc } from '@klear/ui/lib/trpc'
import { PaymentNetworkOptions, PAYMENT_TYPE_READABLE_NAMES } from '@klear/payment/constants'
import { paymentIsFailed } from '@klear/payment/status'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'
import { PaymentDocuments } from '@/app/payments/payment-documents'

export default function PaymentDetail() {
  const { paymentId } = useParams<{ paymentId: string }>()
  const id = parseInt(paymentId || '0')

  const {
    data: paymentData,
    isLoading,
    error
  } = trpc.payment.get.useQuery(
    { id },
    {
      enabled: !!paymentId
    }
  )
  useDocumentTitle(paymentData?.idDisplay ?? 'Payment')

  if (isLoading) {
    return <div className="container mx-auto px-4 py-8 text-center">Loading payment details...</div>
  }

  if (error || !paymentData) {
    return <div className="container mx-auto px-4 py-8 text-center text-red-500">Error loading payment</div>
  }

  const paymentStatus = paymentData.status || 'pending'
  const paymentType = paymentData.paymentType
  const paymentTypeLabel = paymentType != null ? (PAYMENT_TYPE_READABLE_NAMES[paymentType] ?? paymentType) : '—'

  return (
    <div className="flex flex-col space-y-8 p-4 md:flex">
      <div className="flex items-center justify-between space-y-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Payment Details</h2>
          <p className="text-muted-foreground">Payment ID: {paymentData.idDisplay}</p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Payment Information</CardTitle>
            <CardDescription>Basic payment details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-muted-foreground">Payment ID</Label>
              <div className="font-medium">{paymentData.idDisplay}</div>
            </div>
            <div>
              <Label className="text-muted-foreground">Status</Label>
              <div className="mt-1">
                <PaymentStatusBadge status={paymentStatus} />
              </div>
            </div>
            {paymentIsFailed(paymentStatus) && (
              <div>
                <Label className="text-muted-foreground">Failure reason</Label>
                <div className="mt-1 font-medium text-destructive text-sm">{paymentData.stripePaymentErrorMessage}</div>
              </div>
            )}
            <div>
              <Label className="text-muted-foreground">Type</Label>
              <div className="font-medium">{paymentTypeLabel}</div>
            </div>
            <div>
              <Label className="text-muted-foreground">Network</Label>
              <div className="font-medium">
                {PaymentNetworkOptions.find((n) => n.value === paymentData.paymentNetwork)?.label ||
                  paymentData.paymentNetwork}
              </div>
            </div>
            <div>
              <Label className="text-muted-foreground">Amount</Label>
              <div className="font-medium">{paymentData.amountFormatted}</div>
            </div>
            <div>
              <Label className="text-muted-foreground">Effective Date</Label>
              <div className="font-medium">{paymentData.effectiveDate?.toString() ?? '-'}</div>
            </div>
            {paymentData.scheduledDate && (
              <div>
                <Label className="text-muted-foreground">Scheduled Date</Label>
                <div className="font-medium">{paymentData.scheduledDate.toString()}</div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
            <CardDescription>Source and destination accounts</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-muted-foreground">From Organization</Label>
              <div className="font-medium">{paymentData.sourceAccountOrganizationName || '—'}</div>
            </div>
            <div>
              <Label className="text-muted-foreground">From Account Type</Label>
              <div className="font-medium">{paymentData.sourceAccountType || '—'}</div>
            </div>
            <div>
              <Label className="text-muted-foreground">To Organization</Label>
              <div className="font-medium">{paymentData.destinationAccountOrganizationName || '—'}</div>
            </div>
            <div>
              <Label className="text-muted-foreground">To Account Type</Label>
              <div className="font-medium">{paymentData.destinationAccountType || '—'}</div>
            </div>
          </CardContent>
        </Card>

        {(paymentData.orderId || paymentData.orderEventId) && (
          <Card>
            <CardHeader>
              <CardTitle>Related Order</CardTitle>
              <CardDescription>Order and event information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {paymentData.orderId != null && paymentData.orderIdDisplay && (
                <div>
                  <Label className="text-muted-foreground">Order ID</Label>
                  <div className="font-medium">
                    <Link to={`/${ROUTE_ORDERS}/${paymentData.orderIdDisplay}`} className="underline">
                      {paymentData.orderIdDisplay}
                    </Link>
                  </div>
                </div>
              )}
              {paymentData.orderEventIdDisplay && (
                <div>
                  <Label className="text-muted-foreground">Event ID</Label>
                  <div className="font-medium">
                    <Link to={`/${ROUTE_ORDER_EVENTS}/${paymentData.orderEventIdDisplay}`} className="underline">
                      {paymentData.orderEventIdDisplay}
                    </Link>
                  </div>
                </div>
              )}
              {paymentData.orderEventType && (
                <div>
                  <Label className="text-muted-foreground">Event Type</Label>
                  <div className="font-medium">{paymentData.orderEventType}</div>
                </div>
              )}
              {paymentData.orderEventId && (
                <div>
                  <Label className="text-muted-foreground">Order Event ID</Label>
                  <div className="font-medium">{paymentData.orderEventId}</div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <PaymentDocuments paymentId={id} />

        <Card>
          <CardHeader>
            <CardTitle>Additional Information</CardTitle>
            <CardDescription>Notes and external references</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-muted-foreground">Payment Provider</Label>
              <div className="font-medium">{paymentData.paymentProviderName}</div>
            </div>
            {paymentData.stripePaymentId && (
              <div>
                <Label className="text-muted-foreground">Stripe Payment ID</Label>
                <div className="font-medium font-mono text-sm">{paymentData.stripePaymentId}</div>
              </div>
            )}
            {paymentData.notes && (
              <div>
                <Label className="text-muted-foreground">Notes</Label>
                <div className="mt-1 whitespace-pre-wrap">{paymentData.notes}</div>
              </div>
            )}
            {paymentData.internalNotes && (
              <div>
                <Label className="text-muted-foreground">Internal Notes</Label>
                <div className="mt-1 whitespace-pre-wrap">{paymentData.internalNotes}</div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Metadata</CardTitle>
            <CardDescription>Timestamps and tracking information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-muted-foreground">Created At</Label>
              <div className="font-medium">{paymentData.createdAt?.toKlearDate().toString() ?? '-'}</div>
            </div>
            <div>
              <Label className="text-muted-foreground">Updated At</Label>
              <div className="font-medium">{paymentData.updatedAt?.toKlearDate().toString() ?? '-'}</div>
            </div>
            {paymentData.createdByEmail && (
              <div>
                <Label className="text-muted-foreground">Created By</Label>
                <div className="font-medium">{paymentData.createdByEmail}</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
